import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useStore, formatPrice } from "../../context/StoreContext";
import { SIZES } from "../../data/products";

const QuickBuySheet = () => {
  const { quickBuy, setQuickBuy, addToCart, currency } = useStore();
  const [size, setSize] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (quickBuy) setSize(null);
  }, [quickBuy]);

  useEffect(() => {
    if (!quickBuy) return;
    const onKey = (e) => {
      if (e.key === "Escape") setQuickBuy(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [quickBuy, setQuickBuy]);

  if (!quickBuy) return null;

  const handleAdd = (thenCheckout = false) => {
    if (!size) {
      toast("PICK A SIZE FIRST");
      return;
    }
    addToCart(quickBuy, size, 1);
    toast(`${quickBuy.name.toUpperCase()} / SIZE ${size} ADDED`);
    setQuickBuy(null);
    if (thenCheckout) navigate("/checkout");
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Quick buy"
      data-testid="quickbuy-sheet"
      className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-ink/60 backdrop-blur-sm animate-[fade-in_0.2s_ease-out]"
      onClick={() => setQuickBuy(null)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-cream w-full md:max-w-[520px] rounded-t-3xl md:rounded-3xl shadow-[0_-12px_40px_rgba(15,14,13,0.18)] md:shadow-[0_24px_60px_rgba(15,14,13,0.28)] relative"
      >
        {/* drag handle */}
        <div className="md:hidden flex justify-center pt-2 pb-1">
          <div className="w-12 h-1 bg-ink/30" />
        </div>

        <div className="flex items-start gap-4 p-5 md:p-6 border-b border-ink/12">
          <img
            src={quickBuy.images[0]}
            alt={quickBuy.name}
            className="w-20 h-24 object-cover rounded-xl"
          />
          <div className="flex-1 min-w-0">
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink/60">
              {quickBuy.category}
            </p>
            <h3 className="font-display uppercase text-xl md:text-2xl leading-tight mt-1">
              {quickBuy.name}
            </h3>
            <p
              data-testid="quickbuy-price"
              className="font-mono text-sm mt-1 text-ink"
            >
              {formatPrice(quickBuy.priceInr, currency)}
            </p>
          </div>
          <button
            onClick={() => setQuickBuy(null)}
            aria-label="Close"
            data-testid="quickbuy-close"
            className="p-2 hover:text-dc-red"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 md:p-6">
          <p className="font-mono text-[11px] uppercase tracking-widest text-ink/70 mb-2">
            Pick a size
          </p>
          <div className="flex gap-2">
            {SIZES.map((s) => (
              <button
                key={s}
                onClick={() => setSize(s)}
                data-testid={`quickbuy-size-${s}`}
                className={`w-12 h-12 rounded-full font-mono text-sm font-bold transition-colors ${
                  size === s
                    ? "bg-ink text-cream shadow-pill-active"
                    : "bg-ink/5 text-ink hover:bg-ink/10"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <button
              onClick={() => handleAdd(false)}
              data-testid="quickbuy-add"
              className="btn-ghost"
            >
              Add to cart
            </button>
            <button
              onClick={() => handleAdd(true)}
              data-testid="quickbuy-buynow"
              className="btn-solid"
            >
              Buy now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickBuySheet;
